/**
 * State orchestration hook for the Hybrid System Integration Tool.
 * Keeps the visual builder state, parameter sliders, scenario playback,
 * and pure simulation calculations separated from the React view layer.
 * @module HybridSystemIntegration/hooks
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CANVAS_NODE_SIZE,
  CANVAS_SIZE,
  COMPONENT_LIBRARY,
  CONTROL_STRATEGIES,
  DEFAULT_NODES,
  SCENARIOS,
} from './constants';
import {
  buildRecommendations,
  calculateSystemSnapshot,
  createDefaultSettings,
  generateTimeline,
} from './utils';

function clampNodePosition(x, y) {
  return {
    x: Math.max(18, Math.min(CANVAS_SIZE.width - CANVAS_NODE_SIZE.width - 18, x)),
    y: Math.max(18, Math.min(CANVAS_SIZE.height - CANVAS_NODE_SIZE.height - 18, y)),
  };
}

/**
 * Create a new component node for the system canvas.
 * @param {string} type - Component type from the palette.
 * @param {number} index - Creation index used for deterministic positioning.
 * @param {Object} [position] - Optional preferred canvas position.
 * @returns {Object} New node model.
 */
function createNode(type, index, position) {
  const component = COMPONENT_LIBRARY[type];
  const column = index % 3;
  const row = Math.floor(index / 3) % 3;
  const defaultPosition = clampNodePosition(
    95 + column * 245,
    105 + row * 145
  );
  const nextPosition = position
    ? clampNodePosition(position.x, position.y)
    : defaultPosition;

  return {
    id: `node-${type}-${Date.now()}-${index}`,
    type,
    x: nextPosition.x,
    y: nextPosition.y,
    specs: { ...component.defaultSpecs },
  };
}

/**
 * Main custom hook for the Hybrid System Integration Tool.
 * @returns {Object} UI state, computed metrics, and update handlers.
 */
export function useHybridSystemSimulation() {
  const [nodes, setNodes] = useState(() => DEFAULT_NODES.map((node) => ({
    ...node,
    specs: { ...node.specs },
  })));
  const [selectedNodeId, setSelectedNodeId] = useState('node-controller');
  const [settings, setSettings] = useState(createDefaultSettings);
  const [strategy, setStrategy] = useState(CONTROL_STRATEGIES.heatPumpPriority.key);
  const [scenarioKey, setScenarioKey] = useState(SCENARIOS.balanced.key);
  const [storageLevel, setStorageLevel] = useState(SCENARIOS.balanced.storageLevel);
  const [isPlaying, setIsPlaying] = useState(false);

  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) || nodes[0] || null,
    [nodes, selectedNodeId]
  );

  const result = useMemo(
    () => calculateSystemSnapshot({ nodes, settings, strategy, storageLevel }),
    [nodes, settings, strategy, storageLevel]
  );

  const timelineData = useMemo(
    () => generateTimeline({ nodes, settings, strategy, storageLevel }),
    [nodes, settings, strategy, storageLevel]
  );

  const recommendations = useMemo(
    () => buildRecommendations(result, settings),
    [result, settings]
  );

  /**
   * Update one system setting by key.
   * @param {string} key - Setting key.
   * @param {number|string} value - New value.
   */
  const updateSetting = useCallback((key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
  }, []);

  /**
   * Update one selected component specification by key.
   * @param {string} nodeId - Node id.
   * @param {string} key - Spec key.
   * @param {number|string} value - New spec value.
   */
  const updateNodeSpec = useCallback((nodeId, key, value) => {
    setNodes((currentNodes) => currentNodes.map((node) => (
      node.id === nodeId
        ? { ...node, specs: { ...node.specs, [key]: value } }
        : node
    )));
  }, []);

  /**
   * Add a new component to the visual builder canvas.
   * @param {string} type - Component type.
   */
  const addComponent = useCallback((type, position) => {
    if (!COMPONENT_LIBRARY[type]) {
      return;
    }

    setNodes((currentNodes) => {
      const nextNode = createNode(type, currentNodes.length, position);
      setSelectedNodeId(nextNode.id);
      return [...currentNodes, nextNode];
    });
  }, []);

  /**
   * Move a node within the canvas by drag delta.
   * @param {string} nodeId - Node id to move.
   * @param {Object} delta - Drag delta from dnd-kit.
   */
  const moveNode = useCallback((nodeId, delta) => {
    if (!delta || (!delta.x && !delta.y)) {
      return;
    }

    setNodes((currentNodes) => currentNodes.map((node) => {
      if (node.id !== nodeId) {
        return node;
      }

      const nextPosition = clampNodePosition(node.x + delta.x, node.y + delta.y);
      return {
        ...node,
        x: nextPosition.x,
        y: nextPosition.y,
      };
    }));
  }, []);

  /**
   * Remove a non-essential component from the canvas.
   * @param {string} nodeId - Node id to remove.
   */
  const removeComponent = useCallback((nodeId) => {
    setNodes((currentNodes) => {
      const nextNodes = currentNodes.filter((node) => node.id !== nodeId);
      if (selectedNodeId === nodeId) {
        setSelectedNodeId(nextNodes[0]?.id || null);
      }
      return nextNodes;
    });
  }, [selectedNodeId]);

  /**
   * Apply one of the predefined educational scenarios.
   * @param {string} nextScenarioKey - Scenario key.
   */
  const applyScenario = useCallback((nextScenarioKey) => {
    const scenario = SCENARIOS[nextScenarioKey];
    if (!scenario) {
      return;
    }

    setScenarioKey(nextScenarioKey);
    setStrategy(scenario.strategy);
    setStorageLevel(scenario.storageLevel);
    setSettings((current) => ({
      ...current,
      outdoorTemperature: scenario.outdoorTemperature,
      electricityPrice: scenario.electricityPrice,
      gasPrice: scenario.gasPrice,
      electricityCarbonIntensity: scenario.electricityCarbonIntensity,
      solarIrradiance: scenario.solarIrradiance,
    }));
  }, []);

  /**
   * Reset the builder to the default starter hybrid system.
   */
  const resetSystem = useCallback(() => {
    setNodes(DEFAULT_NODES.map((node) => ({ ...node, specs: { ...node.specs } })));
    setSettings(createDefaultSettings());
    setStrategy(CONTROL_STRATEGIES.heatPumpPriority.key);
    setScenarioKey(SCENARIOS.balanced.key);
    setStorageLevel(SCENARIOS.balanced.storageLevel);
    setSelectedNodeId('node-controller');
    setIsPlaying(false);
  }, []);

  useEffect(() => {
    if (!isPlaying) {
      return undefined;
    }

    const timerId = setInterval(() => {
      setSettings((current) => ({
        ...current,
        hourOfDay: (current.hourOfDay + 1) % 24,
      }));
    }, 900);

    return () => clearInterval(timerId);
  }, [isPlaying]);

  return {
    nodes,
    selectedNode,
    selectedNodeId,
    settings,
    strategy,
    scenarioKey,
    storageLevel,
    isPlaying,
    result,
    timelineData,
    recommendations,
    addComponent,
    applyScenario,
    moveNode,
    removeComponent,
    resetSystem,
    setIsPlaying,
    setSelectedNodeId,
    setStorageLevel,
    setStrategy,
    updateNodeSpec,
    updateSetting,
  };
}
